import * as React from 'react'
import { Contact, ContactColumnConfig, ContactColumnProperty, ContactColumns, ContactProperty, ContactType, ResourceRendererOptions, ResourceRendererResult, VATLiability, RateType } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import ResourceTableColor from './ResourceTableColor'
import showdown from '../../utilities/showdown'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'
import styled from 'styled-components'
import ContactHelper from '../../helpers/ContactHelper'
import Icon from '../Icons/Icon'
import Badge from '../Badge/Badge'
import ContactTypeAvatar from '../Avatar/ContactTypeAvatar'
import { Link } from 'react-router-dom'
import RouteHelper from '../../helpers/RouteHelper'
import ERoute from '../../ERoute'

const ContactDetailsContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const ContactDetails = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  color: #333333 !important;
  align-items: flex-end;

  a {
    display: flex;
    flex-direction: row;
    align-items: center;

    svg {
      width: 15px;
      height: 15px;
      margin-right: 10px;
      fill: #829AB1;
    }

    &:hover {
      text-decoration: underline;
    }
  }
`

const ContactGroups = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  margin-top: 4px;
`

const StarContact = styled.div<{ starred: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: ${props => props.starred ? '#f4a230' : 'black'};
  margin-left: 8px;

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`

const ContactAvatarAndName = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  >:first-child {
    margin-right: 8px;
  }
`

const ContactNameAndMetadata = styled.div`
  display: flex;
  flex-direction: column;
`

const ContactName = styled(Link)`
  display: flex;
  flex-direction: row;
  font-weight: 500;
  color: black;

  &:hover {
    text-decoration: underline;
  }
`

const ContactMetadata = styled.div`
  display: flex;
  margin-top: -4px;
  color: #333333 !important;

  a {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: 4px;

    svg {
      width: 15px;
      height: 15px;
      margin-right: 4px;
    }

    &:hover {
      text-decoration: underline;
    }
  }
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: #f5a623;
`

export const DEFAULT_CONTACT_COLUMNS: ContactColumnProperty[] = [
  'name',
  'groups',
  'emails',
  'telephone_numbers',
  'websites',
]

const ContactColumnConfigs: Record<ContactColumnProperty, ContactColumnConfig> = {
  name: {
    header: () => ({ title: i18n.t('Contact::name') }),
    render: ({ contact }) => {
      return (
        <ContactAvatarAndName>
          <ContactTypeAvatar
            onClick={() => { }}
            contact={contact}
            rounded={true}
            width={40}
          />
          <ContactNameAndMetadata>
            <ContactName
              to={RouteHelper.process(ERoute.PATH_CONTACT, { id: contact?.id })}
              onClick={(e) => e.stopPropagation()}
            >
              {contact.name}

              {contact.starred && <StarContact starred={contact.starred}>
                <Icon icon='star' />
              </StarContact>}
            </ContactName>
            <ContactMetadata>
              {contact.type === ContactType.PERSON && contact.contact_id && <>
                {i18n.t('Contacts::Works at')} <Link to={RouteHelper.process(ERoute.PATH_CONTACT, { id: contact?.contact.id })} onClick={(e) => e.stopPropagation()}>{contact?.contact?.name}</Link>
              </>}
            </ContactMetadata>
          </ContactNameAndMetadata>
        </ContactAvatarAndName>
      )
    },
  },
  type: {
    header: () => ({ title: i18n.t('Contact::type') }),
    render: ({ contact }) => contact.type ? ContactHelper.getContactLabelType(contact.type) : '-'
  },
  background_info: {
    header: () => ({ title: i18n.t('Contact::background_info') }),
    render: ({ contact }) => contact.background_info ? <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(contact.background_info) }} /> : '-'
  },
  alias: {
    header: () => ({ title: i18n.t('Contact::alias') }),
    render: ({ contact }) => contact.alias || '-'
  },
  color: {
    header: () => ({ title: i18n.t('Contact::color') }),
    render: ({ contact }) => contact.color ? <ResourceTableColor style={{ backgroundColor: contact.color }} /> : '-'
  },
  locale: {
    header: () => ({ title: i18n.t('Contact::locale') }),
    render: ({ contact }) => contact.locale || '-'
  },
  currency: {
    header: () => ({ title: i18n.t('Contact::currency') }),
    render: ({ contact }) => contact.currency || '-'
  },
  vat_number: {
    header: () => ({ title: i18n.t('Contact::vat_number') }),
    render: ({ contact }) => contact.vat_number || '-'
  },
  notes: {
    header: () => ({ title: i18n.t('Contact::notes') }),
    render: ({ contact }) => contact.notes ? <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(contact.notes) }} /> : '-'
  },
  referral_source: {
    header: () => ({ title: i18n.t('Contact::referral_source') }),
    render: ({ contact }) => contact.referral_source?.name || '-'
  },
  hourly_rate: {
    header: () => ({ title: i18n.t('Contact::hourly_rate') }),
    render: ({ contact, setting }) => contact.hourly_rate ? NumberFormatter.formatCurrency(contact.currency || setting.default_currency, setting.number_format, contact.hourly_rate) : '-'
  },
  day_rate: {
    header: () => ({ title: i18n.t('Contact::day_rate') }),
    render: ({ contact, setting }) => contact.day_rate ? NumberFormatter.formatCurrency(contact.currency || setting.default_currency, setting.number_format, contact.day_rate) : '-'
  },
  archived: {
    header: () => ({ title: i18n.t('Contact::archived') }),
    render: ({ contact }) => contact.archived ? i18n.t('Yes') : i18n.t('No')
  },
  vat_liability: {
    header: () => ({ title: i18n.t('Contact::vat_liability') }),
    render: ({ contact }) => contact.vat_liability ? i18n.t(`VATLiability::${contact.vat_liability}`) : '-'
  },
  rate_type: {
    header: () => ({ title: i18n.t('Contact::rate_type') }),
    render: ({ contact }) => contact.rate_type ? i18n.t(`RateType::${contact.rate_type}`) : '-'
  },
  peppol_id: {
    header: () => ({ title: i18n.t('Contact::peppol_id') }),
    render: ({ contact }) => contact.peppol_id || '-'
  },
  emails: {
    header: () => ({ title: i18n.t('Contact::emails') }),
    render: ({ contact }) => {
      if (!contact.emails || contact.emails.length === 0) return '-'

      return (
        <ContactDetailsContainer>
          <ContactDetails>
            {contact.emails.slice(0, 1).map((email, index) => (
              <a key={index} href={`mailto:${email.value}`} onClick={(e) => e.stopPropagation()}>
                <Icon icon='email' />
                {email.value}
              </a>
            ))}
            {contact.emails.length > 1 && <span>(+{contact.emails.length - 1})</span>}
          </ContactDetails>
        </ContactDetailsContainer>
      )
    }
  },
  telephone_numbers: {
    header: () => ({ title: i18n.t('Contact::telephone_numbers') }),
    render: ({ contact }) => {
      if (!contact.telephone_numbers || contact.telephone_numbers.length === 0) return '-'
      return (
        <ContactDetailsContainer>
          <ContactDetails>
            {contact.telephone_numbers.slice(0, 1).map((phone, index) => (
              <a key={index} href={`tel:${phone.value}`} onClick={(e) => e.stopPropagation()}>
                <Icon icon='phone' />
                {phone.value}
              </a>
            ))}
            {contact.telephone_numbers.length > 1 && <span>(+{contact.telephone_numbers.length - 1})</span>}
          </ContactDetails >
        </ContactDetailsContainer>
      )
    }
  },
  account_numbers: {
    header: () => ({ title: i18n.t('Contact::account_numbers') }),
    render: ({ contact }) => {
      if (!contact.account_numbers || contact.account_numbers.length === 0) return '-'
      return (
        <ContactDetailsContainer>
          <ContactDetails>
            {contact.account_numbers.slice(0, 1).map((account, index) => (
              <span key={index}>{account.value}</span>
            ))}
            {contact.account_numbers.length > 1 && <span>(+{contact.account_numbers.length - 1})</span>}
          </ContactDetails>

        </ContactDetailsContainer>
      )
    }
  },
  billing_address: {
    header: () => ({ title: i18n.t('Contact::billing_address') }),
    render: ({ contact }) => contact.billing_address ? ContactHelper.getFullAddress(contact.billing_address) : '-'
  },
  delivery_address: {
    header: () => ({ title: i18n.t('Contact::delivery_address') }),
    render: ({ contact }) => contact.delivery_address ? ContactHelper.getFullAddress(contact.delivery_address) : '-'
  },
  websites: {
    header: () => ({ title: i18n.t('Contact::websites') }),
    render: ({ contact }) => {
      if (!contact.websites || contact.websites.length === 0) return '-'

      return (
        <ContactDetailsContainer>
          <ContactDetails>
            {contact.websites.slice(0, 1).map((website, index) => (
              <a key={index} href={website.url} onClick={(e) => e.stopPropagation()}>
                <Icon icon='globe' />
                {new URL(website.url).host}
              </a>
            ))}
            {contact.websites.length > 1 && <span>+{contact.websites.length - 2}</span>}
          </ContactDetails>
        </ContactDetailsContainer>
      )
    }
  },
  groups: {
    header: () => ({ title: i18n.t('Contact::groups') }),
    render: ({ contact }) => {
      if (!contact.groups || contact.groups.length === 0) return '-'

      return (
        <ContactGroups>
          {contact.groups.sort((g1, g2) => (g1.name.localeCompare(g2.name))).map(group => (<Badge type='grey' text={group.name} />))}
        </ContactGroups>
      )
    }
  },
  starred: {
    header: () => ({ title: i18n.t('Contact::starred') }),
    render: ({ contact }) => (
      <StarContact starred={contact.starred}>
        <Icon icon={contact.starred ? 'star' : 'star-empty'} />
      </StarContact>
    )
  },
  marketing_consent: {
    header: () => ({ title: i18n.t('Contact::marketing_consent') }),
    render: ({ contact }) => contact.marketing_consent ? i18n.t('Yes') : i18n.t('No')
  },
  created_at: {
    header: () => ({ title: i18n.t('Contact::created_at') }),
    render: ({ contact, setting }) => contact.created_at ? moment(contact.created_at).format(setting.date_format) : '-'
  },
  first_name: {
    header: () => ({ title: i18n.t('Contact::first_name') }),
    render: ({ contact }) => contact.type === ContactType.PERSON ? (contact as any).first_name || '-' : '-'
  },
  last_name: {
    header: () => ({ title: i18n.t('Contact::last_name') }),
    render: ({ contact }) => contact.type === ContactType.PERSON ? (contact as any).last_name || '-' : '-'
  },
  job_title: {
    header: () => ({ title: i18n.t('Contact::job_title') }),
    render: ({ contact }) => contact.type === ContactType.PERSON ? (contact as any).job_title || '-' : '-'
  },
  birthdate: {
    header: () => ({ title: i18n.t('Contact::birthdate') }),
    render: ({ contact, setting }) => {
      if (contact.type !== ContactType.PERSON) return '-'
      const birthdate = (contact as any).birthdate
      return birthdate ? moment(birthdate).format(setting.date_format) : '-'
    }
  },
}

export function getContactTableRenderer(options: ResourceRendererOptions<Contact>): ResourceRendererResult<Contact, ContactProperty> {
  const {
    setting,
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = ContactColumns.map(column => ({ label: i18n.t(`Contact::${column}`), value: column }))
  const selectedColumns: ContactProperty[] = LocalStorage
    .get(LocalStorageKey.CONTACT_COLUMNS, DEFAULT_CONTACT_COLUMNS)
    .filter((column) => ContactColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...ContactColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (contact: Contact, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(contact.id)
    let actions: IActionListItem[] = [
      { key: 'archive', icon: 'archive', content: contact.archived ? i18n.t('ContactsTable::Unarchive') : i18n.t('ContactsTable::Archive') },
      { key: 'edit', icon: 'edit-solid', content: i18n.t('ContactsTable::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('ContactsTable::Delete'), destructive: true }
    ]

    const applyMaxWidthColumns: ContactProperty[] = [
      'name',
      'alias',
      'background_info',
      'notes',
      'billing_address',
      'delivery_address',
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => (
      <ResourceTableRowData
        key={`${contact.id}-${column}`}
        onClick={onRowClick ? () => onRowClick(contact) : undefined}
        maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
        ellipse={applyMaxWidthColumns.includes(column)}
        textAlign={ContactColumnConfigs[column].header().align}
      >
        {ContactColumnConfigs[column].render({ contact, ...options })}
      </ResourceTableRowData>
    ))

    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${contact.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, contact)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={contact.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, contact.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getContactTableRenderer
